import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, Brain, Eye, Compass, Heart, Shield, Gift, PlayCircle, Download, BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { getJournal, playerTransactions } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import StripeDialog from "@/components/StripeDialog";
import BetaCodeInput from "@/components/BetaCodeInput";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";

export default function Parental() {
  const { t, i18n } = useTranslation();
  const { player, setPlayer } = usePlayer();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const eveilDone = player && [101, 102].every((id) => (player.levels_completed || []).includes(id));
  const [journal, setJournal] = useState(null);
  const [err, setErr] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const SKILL_ICONS = {
    logic: { label: t("parental.skill_logic"), icon: Brain, color: "text-amber-300" },
    visual_memory: { label: t("parental.skill_visual"), icon: Eye, color: "text-emerald-300" },
    planning: { label: t("parental.skill_planning"), icon: Compass, color: "text-teal-300" },
    patience: { label: t("parental.skill_patience"), icon: Heart, color: "text-rose-300" },
  };

  useEffect(() => {
    if (!player?.id) return;
    playerTransactions(player.id).then(setTransactions).catch(() => {});
  }, [player, payOpen]);

  useEffect(() => {
    if (!player?.id) return;
    getJournal(player.id).then(setJournal).catch(() => {
      setErr(t("parental.load_error"));
      const p = player;
      const puzzles = p.puzzles_solved || (p.levels_completed || []).length;
      setJournal({
        player_name: p.name,
        mode: p.mode,
        total_playtime_minutes: Math.round((p.playtime_seconds || 0) / 60),
        puzzles_solved: puzzles,
        hints_used: p.hints_used || 0,
        levels_completed: p.levels_completed || [],
        relics_unlocked: p.relics_unlocked || [],
        grimoire_pages: p.grimoire_pages || [],
        memory_shards: p.memory_shards || 0,
        cognitive_skills: {
          logic: Math.min(100, puzzles * 22),
          visual_memory: Math.min(100, (p.grimoire_pages || []).length * 25),
          planning: Math.min(100, puzzles * 18),
          patience: Math.min(100, 50 + puzzles * 15 - (p.hints_used || 0) * 5),
        },
      });
    });
  }, [player, t]);

  if (!player) {
    return (
      <div className="min-h-screen hero-radial grain flex items-center justify-center p-6">
        <div className="glass-strong rounded-3xl p-8 max-w-md text-center">
          <Shield className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
          <h2 className="font-serif text-2xl font-bold">{t("parental.no_profile_title")}</h2>
          <p className="text-stone-300 mt-2">{t("parental.no_profile_text")}</p>
          <Link to="/profile" className="inline-block mt-4 text-amber-300 hover:text-amber-200 font-bold" data-testid="parental-go-profile">{t("parental.no_profile_cta")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Shield className="w-5 h-5 text-emerald-300" /> {t("parental.header")}
        </div>
        <SoundToggle />
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pb-20">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/90 font-bold">{t("parental.kicker")}</div>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">
            {t("parental.title_1")}<span className="italic text-amber-300">{journal?.player_name || player.name}</span>
          </h2>
          <p className="mt-2 text-stone-300 max-w-2xl">{t("parental.subtitle")}</p>
        </div>

        {err && <div className="glass rounded-2xl p-3 text-amber-200 mb-4 text-sm">{err}</div>}

        {journal && (
          <>
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="journal-bento">
              {[
                { label: t("parental.stats_playtime"), value: `${journal.total_playtime_minutes} ${t("parental.playtime_unit")}`, color: "text-amber-300" },
                { label: t("parental.stats_puzzles"), value: journal.puzzles_solved, color: "text-emerald-300" },
                { label: t("parental.stats_hints"), value: journal.hints_used, color: "text-teal-300" },
                { label: t("parental.stats_shards"), value: journal.memory_shards, color: "text-rose-300" },
              ].map((s) => (
                <div key={s.label} className="glass rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{s.label}</div>
                  <div className={`mt-1 font-serif text-3xl font-black ${s.color}`}>{s.value}</div>
                </div>
              ))}
            </section>

            <section className="glass rounded-3xl p-6 mb-8" data-testid="journal-skills">
              <h3 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("parental.skills_title")}</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                {Object.entries(journal.cognitive_skills).map(([k, v]) => {
                  const meta = SKILL_ICONS[k];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${meta.color}`} />
                          <span className="font-sans font-bold text-stone-200 text-sm">{meta.label}</span>
                        </div>
                        <span className="font-mono text-xs text-stone-400">{v}%</span>
                      </div>
                      <Progress value={v} className="h-2 bg-stone-800" />
                    </div>
                  );
                })}
              </div>
              <p className="mt-6 text-xs text-stone-400 leading-relaxed">{t("parental.skills_note")}</p>
            </section>

            <section className="glass rounded-3xl p-6" data-testid="journal-safety">
              <h3 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("parental.safety_title")}</h3>
              <ul className="space-y-2 text-sm text-stone-300">
                <li>{t("parental.safety_1")}</li>
                <li>{t("parental.safety_2")}</li>
                <li>{t("parental.safety_3")}</li>
                <li>{t("parental.safety_4")}</li>
              </ul>
            </section>

            <section className="glass rounded-3xl p-6 mt-6" data-testid="journal-tools">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-teal-300/90 font-bold">{t("parental.tools_kicker")}</div>
                  <h3 className="mt-1 font-serif text-xl font-bold text-stone-50">{t("parental.tools_title")}</h3>
                  <p className="mt-1 text-sm text-stone-300 max-w-xl">{t("parental.tools_desc")}</p>
                </div>
                <Button
                  onClick={() => window.dispatchEvent(new Event("chronos:replay-onboarding"))}
                  variant="outline"
                  className="border-teal-400/50 bg-transparent text-teal-200 hover:bg-teal-900/20 rounded-full px-5"
                  data-testid="btn-replay-onboarding"
                >
                  <PlayCircle className="w-4 h-4 mr-2" /> {t("parental.tools_replay")}
                </Button>
              </div>
            </section>
            {eveilDone && (
              <section className="glass rounded-3xl p-6 mt-6" data-testid="journal-passport">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("parental.passport_kicker")}</div>
                    <h3 className="mt-1 font-serif text-xl font-bold text-stone-50">{t("parental.passport_title")}</h3>
                    <p className="mt-1 text-sm text-stone-300 max-w-xl">{t("parental.passport_desc")}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <a href={`${API_URL}/api/eveil/passport/${player.id}/certificate.pdf?lang=${i18n.language?.slice(0,2) || "fr"}`} target="_blank" rel="noopener noreferrer" data-testid="parental-dl-cert">
                      <Button className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-5">
                        <Download className="w-4 h-4 mr-2" /> {t("parental.passport_cert")}
                      </Button>
                    </a>
                    <a href={`${API_URL}/api/eveil/parent-guide.pdf?lang=${i18n.language?.slice(0,2) || "fr"}`} target="_blank" rel="noopener noreferrer" data-testid="parental-dl-guide">
                      <Button variant="outline" className="border-emerald-400/50 bg-transparent text-emerald-200 hover:bg-emerald-900/20 rounded-full px-5">
                        <BookOpen className="w-4 h-4 mr-2" /> {t("parental.passport_guide")}
                      </Button>
                    </a>
                  </div>
                </div>
              </section>
            )}

            <BetaCodeInput />

            <section className="mt-6 relative overflow-hidden rounded-3xl border border-amber-400/25 p-6" data-testid="parental-shop">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-stone-900/60 to-rose-900/20" aria-hidden />
              <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5" /> {t("parental.shop_kicker")}
                  </div>
                  <h3 className="mt-1 font-serif text-2xl font-bold text-stone-50">{t("parental.shop_title")}</h3>
                  <p className="mt-1 text-sm text-stone-300 max-w-xl">
                    {t("parental.shop_desc")}
                    {player.premium_tier && (
                      <span className="ml-2 text-emerald-300 font-bold">{t("parental.shop_status", { tier: player.premium_tier })}</span>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => setPayOpen(true)}
                  className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6"
                  data-testid="open-paypal-dialog"
                >
                  <Gift className="w-4 h-4 mr-2" /> {t("parental.shop_open")}
                </Button>
              </div>

              {transactions.length > 0 && (
                <div className="mt-5 relative z-10 space-y-2" data-testid="tx-list">
                  <div className="text-xs uppercase tracking-widest font-bold text-stone-300">{t("parental.shop_history")}</div>
                  {transactions.slice(0, 4).map((tx) => (
                    <div key={tx.session_id} className="flex items-center justify-between text-sm glass rounded-xl px-3 py-2">
                      <span className="font-sans text-stone-200">{tx.lookup_key} · {(tx.amount / 100).toFixed(2)} {tx.currency?.toUpperCase()}</span>
                      <span className={`text-xs font-bold ${tx.payment_status === "paid" ? "text-emerald-300" : "text-amber-300"}`}>{tx.payment_status}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <StripeDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        playerId={player.id}
      />
    </div>
  );
}
